import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { deleteDoc, doc } from 'firebase/firestore';
import Swal from 'sweetalert2';
import ReactTooltip from 'react-tooltip';
import { DB } from '~/services/firebase';

import Dashboard from '~/layouts/Dashboard';
import { useToast } from '~/hooks/toast';
import IProduct from '~/interfaces/IProduct';

import AWSBucket, { AWSParams } from '~/services/aws';
import { useProductsCompany } from '~/hooks/productsCompany';
import getCurrencyFormatted from '~/utils/getCurrencyFormatted';

const {
  REACT_APP_AWS_BUCKET_URL,
  REACT_APP_USE_API_PRODUCTS,
  REACT_APP_PAGINATE_PRODUCTS,
} = process.env;

const placeholder =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAARMAAAC3CAMAAAAGjUrGAAAAMFBMVEXx8/XCy9K/yND09vfw8vTP1tzp7O/i5ure4+fO1dvJ0dfT2d/EzNPt7/Lb4OXo6+4FeM7UAAAFL0lEQVR4nO2c24KrIAxFLdha7///t0dxOlWDSiAKztnrbR4G6SoJBKHZA6zJYncgQeCEAicUOKHACQVOKHBCgRMKnFDghAInFDihwAkFTihwQoETCpxQ4IQCJxQ4ocAJBU4ocEKBEwqcUOCEAicUOKHACQVOKHBCgRMKnFDghAInFDihwAkFTihwQoETCpxQ4IQCJxQ4ocAJBU4ot3Oi1KMq64FnWTVq+EueWzlRquqKVn/J+/ezEfdyHydKPYtc62yF1m1Xymq5ixPVdDnx8eslf1eCVu7hRFXFppAfLW39kNJyByeqOTJirGTvRsbKDZyozsHIpKUQsZK8E1Vu55GTrKTuRL0ZRoyVLviZaTtRVctUMuaVOnCoJO1E1WwjxsorbGZO2Qk7br5WuhApKTvpfZWMy5WAoZKuk6b1NhI4VJJ10uRBSsas0ng+OlUnVaARw9NvqCTqRERJpt9eUtJ0IqPEN36SdNIIKRnIPeafFJ0Ep9c5mr+qTdFJ2CRMpLAn5fScqJeokrFWZkoRdaImwtpw2T9iSnnxuiDoRFXda6hK28JzWTA14ryBxKFlTT9iTlT1W57o3Lta96yED8krRieknCw/DDuEP1TnKBlgzMlCTtZDXr+8pIjOwitK5x7JOKFD3mukiE85ix45S5FxYll46prdiv8ekpsU19wv4kS9LV1ouQPlrPzKliIzTuw9YDYiVfgFSxFx8rR+wcyMomSX9HYpTjlFwonqrB3gBc/JyYQjRcRJYe8Ay4l9rMlLcVi8iTjp7Y/nOBHcMjngWEoi4+TUlcmKw9rnxHzCWMqeU/ltkB9JEZl3SusnYmwQn1fm2GgPeiOzZrM9WZfu/3/BNDznYATLOLENffep+JppeMZBMSZUF9N6ljFM7KF3qpTduBZyQj4W53XTiRsEm1L2dr2k9k9W9Rtjq2BrJj9Zyk7pI7bP9lw8kfH+4KIFLGF77Sa3R90Un0POvHNCcYzsLVMk9+2buni1bd9xjMSJHMPmjCz7zov/fidW5GQ7OS/2e8BoRrLtrBfXScTIMVLsk09cJxEjZ8I6+cR1EmG1tsRaDsZ0EjlyDL0leuxOpulD4JTALtfXORRbnqVO1LDOePdtpoclWPsqulL+wt0P0SNnxFKrrp2opmuXl+5OuHA3PSmByDGQ9ezSydYdM+ELd4YUIsdANnoWTva2RSUv3JlnJRE5I2RbY+6kee1+dTrrhC7cPTZeMUdivZnydaIc3tdqqWuI6USOYZlSfp0oxzVlJxNByUSOYZlSPk6cDzqEXy17JDTn/LBMKRlTSRZ4X2giep2zZnEwZHLiGjifFt6BTtKKHMMspUxO2BkvDzoDm1jkGGa7bsaJx0t9XfgrOfuMlhezwsc48RrKufvhyiXXHatg8T2Zkm0eHzluxO8W4pXHKljkXycBt3h9blFdeqyCx2fPOguLbn6qTWsBu+Czxs/CopsdP4kmkx+mcZ8FRrfuWUqSTSYT005keDucW4iXnzRhMg17iYacC6A0VyZzzIQs0pBrUrn22JoXY4Us0pDjaZMzb+dIMX6/Qi0dHSU0XHySz48heqSaOs60vsvlq2mtpzj9OCh/Trgjew7afgLar63d6ec2SmTZm37+UyV7048K+Gmkm7O10A/8aaSbY7sEr8rYvYoNnX4Sr3EuYJVpVc35Ccu/innZbryMJ1n4v9f4N9FZ39XPZ931GYzMGH9VPHYfAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAADp8Q9+nG9anuOrfAAAAABJRU5ErkJggg==';

let interval: any;

const orderTypeMap = {
  name: 'Nome',
  order: 'Ordem',
};

const Products: React.FC = () => {
  const { addToast } = useToast();
  const { products, loadProducts, removeProduct } = useProductsCompany();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('Todos');
  const [showFilter, setShowFilter] = useState(false);
  const [orderType, setOrderType] = useState<'name' | 'order'>('name');

  const handleChangeSearch = useCallback((value: string) => {
    if (interval) clearInterval(interval);

    interval = setInterval(() => {
      setSearch(value);
    }, 1000);
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const filteredProducts = useMemo((): any => {
    if (REACT_APP_PAGINATE_PRODUCTS) {
      const filtered = [] as IProduct[];

      for (let index = 0; index < products.length; index += 1) {
        let result = true;
        const product = products[index];

        if (status === 'Ativo') {
          result =
            product.name
              .toLocaleLowerCase()
              .includes(search.toLocaleLowerCase()) && product.status === true;
        } else if (status === 'Inativo') {
          result =
            product.name
              .toLocaleLowerCase()
              .includes(search.toLocaleLowerCase()) && product.status === false;
        } else if (status === 'Estoque Baixo') {
          result =
            product.name
              .toLocaleLowerCase()
              .includes(search.toLocaleLowerCase()) &&
            product.stok <= product.stok_min;
        } else {
          result = product.name
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase());
        }

        if (result) {
          filtered.push(product);

          if (filtered.length >= 50) {
            break;
          }
        }
      }

      filtered.sort((a, b) =>
        a[orderType] > b[orderType] ? 1 : b[orderType] > a[orderType] ? -1 : 0,
      );
      return filtered;
    }

    const filtered = products.filter(product => {
      let result = true;

      if (status === 'Ativo') {
        result =
          product.name
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase()) && product.status === true;
      } else if (status === 'Inativo') {
        result =
          product.name
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase()) && product.status === false;
      } else if (status === 'Estoque Baixo') {
        result =
          product.name
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase()) &&
          product.stok <= product.stok_min;
      } else {
        result = product.name
          .toLocaleLowerCase()
          .includes(search.toLocaleLowerCase());
      }

      return result;
    });

    if (orderType === 'name') {
      filtered.sort((a, b) =>
        a.name.toLowerCase().trim() > b.name.toLowerCase().trim()
          ? 1
          : b.name.toLowerCase().trim() > a.name.toLowerCase().trim()
          ? -1
          : 0,
      );
    }

    if (orderType === 'order') {
      filtered.sort((a, b) =>
        (a.order || 999) > (b.order || 999)
          ? 1
          : (b.order || 999) > (a.order || 999)
          ? -1
          : 0,
      );
    }

    return filtered;
  }, [search, products, status, orderType]);

  const handleConfirmRemove = useCallback(
    async (item: IProduct) => {
      const { isConfirmed } = await Swal.fire({
        title: 'Atenção!',
        text: 'Confirme a exclusão do produto?',
        icon: 'warning',
        confirmButtonText: 'Sim',
        showCancelButton: true,
        cancelButtonText: 'Não',
      });

      if (isConfirmed) {
        await deleteDoc(doc(DB, 'products', item.uid));

        removeProduct(item.uid);

        if (item.image) {
          const Key = item.image.replace(`${REACT_APP_AWS_BUCKET_URL}/`, '');
          const config: any = {
            ...AWSParams,
            Key,
          };

          AWSBucket.deleteObject(config, (err, tmp) => {
            if (err) console.log(err);
            else console.log();
          });
        }

        loadProducts();

        addToast({
          type: 'success',
          description: 'Produto foi removida com sucesso.',
        });
      }
    },
    [addToast, loadProducts, removeProduct],
  );

  return (
    <Dashboard containerMessage="col-xl-10 offset-xl-1">
      <div className="col-xl-10 offset-xl-1">
        <div className="nk-block-head nk-block-head-sm">
          <div className="nk-block-between">
            <div className="nk-block-head-content">
              <h3 className="nk-block-title page-title">Produtos</h3>
            </div>
            <div className="nk-block-head-content">
              <div className="toggle-wrap nk-block-tools-toggle">
                <button
                  type="button"
                  className={`btn btn-icon btn-trigger toggle-expand mr-n1 ${
                    showFilter && 'active'
                  }`}
                  data-target="pageMenu"
                  onClick={() => setShowFilter(current => !current)}
                >
                  <em className="icon ni ni-more-v" />
                </button>
                <div
                  className={`toggle-expand-content ${
                    showFilter && 'expanded'
                  }`}
                  data-content="pageMenu"
                >
                  <ul className="nk-block-tools g-3">
                    <li>
                      <div className="form-control-wrap">
                        <div className="form-icon form-icon-right">
                          <em className="icon ni ni-search" />
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          id="default-04"
                          placeholder="Buscar por produto"
                          onChange={event => {
                            handleChangeSearch(event.target.value);
                          }}
                        />
                      </div>
                    </li>
                    <li>
                      <div className="drodown">
                        <Link
                          to="#order-type"
                          className="dropdown-toggle dropdown-indicator btn btn-outline-light btn-white"
                          data-toggle="dropdown"
                        >
                          {orderTypeMap[orderType]}
                        </Link>
                        <div className="dropdown-menu dropdown-menu-right">
                          <ul className="link-list-opt no-bdr">
                            <li>
                              <Link
                                to="#name"
                                onClick={() => setOrderType('name')}
                              >
                                <span>Nome</span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="#order"
                                onClick={() => setOrderType('order')}
                              >
                                <span>Ordem</span>
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="drodown">
                        <Link
                          to="#status"
                          className="dropdown-toggle dropdown-indicator btn btn-outline-light btn-white"
                          data-toggle="dropdown"
                        >
                          {status}
                        </Link>
                        <div className="dropdown-menu dropdown-menu-right">
                          <ul className="link-list-opt no-bdr">
                            <li>
                              <Link
                                to="#status"
                                onClick={() => setStatus('Todos')}
                              >
                                <span>Todos</span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="#active"
                                onClick={() => setStatus('Ativo')}
                              >
                                <span>Ativo</span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="#inactive"
                                onClick={() => setStatus('Inativo')}
                              >
                                <span>Inativo</span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="#stok"
                                onClick={() => setStatus('Estoque Baixo')}
                              >
                                <span>Estoque Baixo</span>
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </li>
                    <li className="nk-block-tools-opt">
                      <Link
                        to="/products/create"
                        data-target="addProduct"
                        className="toggle btn btn-icon btn-primary d-md-none"
                      >
                        <em className="icon ni ni-plus" />
                      </Link>
                      <Link
                        to="/products/create"
                        data-target="addProduct"
                        className="toggle btn btn-primary d-none d-md-inline-flex"
                      >
                        <em className="icon ni ni-plus" />
                        <span>Criar Produto</span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row tb-card">
          {filteredProducts.length === 0 && (
            <div className="col-md-12">
              <div className="card card-bordered">
                <div className="card-inner">
                  <div className="d-flex justify-content-center">
                    Você não possui produtos cadastrados.
                  </div>
                </div>
              </div>
            </div>
          )}

          {filteredProducts.map((item: IProduct) => (
            <div className="col-md-6 col-sm-6 mb-3" key={item.uid}>
              <div className="card card-bordered">
                <div className="card-inner">
                  <div className="team">
                    <div className="user-card user-card-s2">
                      <div className="user-avatar lg bg-primary mb-3">
                        <img src={item.image || placeholder} alt="Tem Pedido" />
                        {!item.status && (
                          <div className="status dot dot-lg dot-danger" />
                        )}
                        {item.status && (
                          <div className="status dot dot-lg dot-success" />
                        )}
                      </div>
                      <div className="mb-3">
                        <h6>{item.name}</h6>
                        <span className="sub-text mb-2">
                          {item.category.name}
                        </span>
                        <Link
                          to={{
                            pathname: `/products/edit/${item.uid}`,
                            state: item,
                          }}
                          className="sub-text mb-2"
                        >
                          <span
                            className={`badge
                              ${!item.stok ? 'badge-warning' : ''}
                              ${
                                item.stok && item.stok > item.stok_min
                                  ? 'badge-success'
                                  : ''
                              }
                              ${
                                item.stok && item.stok <= item.stok_min
                                  ? 'badge-danger'
                                  : ''
                              }
                            `}
                          >
                            {!item.stok && 'CONFIGURAR ESTOQUE'}
                            {item.stok &&
                              item.stok > item.stok_min &&
                              'ESTOQUE NORMAL'}
                            {item.stok &&
                              item.stok <= item.stok_min &&
                              'ESTOQUE BAIXO'}
                          </span>
                        </Link>
                        {item.enable_promotion && (
                          <>
                            <span className="badge badge-success ml-1">
                              Promoção
                            </span>
                            <br />
                          </>
                        )}
                        <span>
                          {getCurrencyFormatted(
                            parseFloat(
                              item.enable_promotion
                                ? item.value_promotion
                                : item.value,
                            ),
                          )}
                        </span>
                        {item.enable_promotion && (
                          <span
                            className="ml-1"
                            style={{
                              textDecoration: 'line-through',
                              fontSize: 10,
                            }}
                          >
                            {getCurrencyFormatted(parseFloat(item.value))}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="team-view">
                      <Link
                        className="btn btn-dim btn-sm btn-primary ml-1"
                        to={{
                          pathname: `/products/edit/${item.uid}`,
                          state: item,
                        }}
                      >
                        <em className="icon ni ni-edit" />
                        <span>Editar</span>
                      </Link>
                      <Link
                        to="#remove"
                        className="btn btn-dim btn-sm btn-danger ml-1"
                        onClick={() => {
                          handleConfirmRemove(item);
                        }}
                      >
                        <em className="icon ni ni-trash" />
                        <span>Excluir</span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="nk-block tb-status">
          <div className="card card-bordered">
            <div className="card-inner-group">
              <div className="card-inner p-0">
                <div className="nk-tb-list">
                  <div className="nk-tb-item nk-tb-head">
                    <div className="nk-tb-col">
                      <span>Ordem</span>
                    </div>
                    <div className="nk-tb-col">
                      <span>Nome</span>
                    </div>
                    <div className="nk-tb-col">
                      <span>Valor</span>
                    </div>
                    <div className="nk-tb-col">
                      <span>Categoria</span>
                    </div>
                    <div className="nk-tb-col">
                      <span>Estoque</span>
                    </div>
                    <div className="nk-tb-col">
                      <span>Status</span>
                    </div>

                    <div className="nk-tb-col nk-tb-col-tools">
                      <ul className="nk-tb-actions gx-1 my-n1">
                        <li className="mr-n1" />
                      </ul>
                    </div>
                  </div>

                  {filteredProducts.map((product: IProduct) => (
                    <div className="nk-tb-item" key={product.uid}>
                      <div className="nk-tb-col">
                        <div className="asterisk tb-asterisk">
                          <span className="fs-14px">{product.order}</span>
                        </div>
                      </div>

                      <div className="nk-tb-col">
                        <span className="tb-product">
                          <img
                            src={product.image ? product.image : placeholder}
                            alt="categoria"
                            className="thumb"
                          />
                          <span className="title">{product.name}</span>
                          <ReactTooltip place="right" effect="solid" />
                          {product.enable_promotion && (
                            <span
                              id={product.uid}
                              data-tip="Promoção"
                              className="badge badge-success ml-1"
                            >
                              %
                            </span>
                          )}
                        </span>
                      </div>

                      <div className="nk-tb-col">
                        <div className="asterisk tb-asterisk">
                          <span className="fs-14px">
                            {getCurrencyFormatted(
                              parseFloat(
                                product.enable_promotion
                                  ? product.value_promotion
                                  : product.value,
                              ),
                            )}
                          </span>
                          {product.enable_promotion && (
                            <span
                              className="fs-10px ml-1"
                              style={{ textDecoration: 'line-through' }}
                            >
                              {getCurrencyFormatted(parseFloat(product.value))}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="nk-tb-col">
                        <div className="asterisk tb-asterisk">
                          <span className="fs-14px">
                            {product.category.name}
                          </span>
                        </div>
                      </div>

                      <div className="nk-tb-col">
                        <div className="asterisk tb-asterisk">
                          <Link
                            to={{
                              pathname: `/products/edit/${product.uid}`,
                              state: product,
                            }}
                          >
                            <span
                              className={`badge
                              ${!product.stok ? 'badge-warning' : ''}
                              ${
                                product.stok && product.stok > product.stok_min
                                  ? 'badge-success'
                                  : ''
                              }
                              ${
                                product.stok && product.stok <= product.stok_min
                                  ? 'badge-danger'
                                  : ''
                              }
                            `}
                            >
                              {!product.stok && 'CONFIGURAR'}
                              {product.stok &&
                                product.stok > product.stok_min &&
                                'NORMAL'}
                              {product.stok &&
                                product.stok <= product.stok_min &&
                                'BAIXO'}
                            </span>
                          </Link>
                        </div>
                      </div>

                      <div className="nk-tb-col">
                        <div className="asterisk tb-asterisk">
                          <span
                            className={`text-${
                              product.status ? 'success' : 'danger'
                            } fs-12px`}
                          >
                            {product.status && <span>Ativo</span>}
                            {!product.status && <span>Inativo</span>}
                          </span>
                        </div>
                      </div>
                      <div className="nk-tb-col nk-tb-col-tools">
                        <ul className="nk-tb-actions gx-1 my-n1">
                          <li className="mr-n1">
                            <div className="dropdown">
                              <Link
                                to="#options"
                                className="dropdown-toggle btn btn-icon btn-trigger"
                                data-toggle="dropdown"
                              >
                                <em className="icon ni ni-more-h" />
                              </Link>
                              <div className="dropdown-menu dropdown-menu-right">
                                <ul className="link-list-opt no-bdr">
                                  <li>
                                    <Link
                                      to={{
                                        pathname: `/products/edit/${product.uid}`,
                                        state: product,
                                      }}
                                    >
                                      <em className="icon ni ni-edit" />
                                      <span>Editar</span>
                                    </Link>
                                  </li>

                                  <li>
                                    <Link
                                      to="#remove"
                                      onClick={() => {
                                        handleConfirmRemove(product);
                                      }}
                                    >
                                      <em className="icon ni ni-trash" />
                                      <span>Excluir</span>
                                    </Link>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default Products;
